import { Loader2, Plus, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import type {
	FieldDescriptor,
	FilterClause,
	PreviewResult,
	SegmentDefinition,
	SegmentSummary,
} from "../../types/planner";
import { FilterRow } from "./FilterRow";
import { ProductRow } from "./ProductRow";
import { PLANNER_PRESETS } from "./presets";

const nf = new Intl.NumberFormat("da-DK");
const DEBOUNCE_MS = 300;

const ErrorBanner = ({ message }: { message: string }) => (
	<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
		{message}
	</div>
);

export const CatalogueTab = () => {
	const [fields, setFields] = useState<FieldDescriptor[]>([]);
	const [fieldsError, setFieldsError] = useState<string | null>(null);
	const [segments, setSegments] = useState<SegmentSummary[]>([]);

	const [match, setMatch] = useState<"all" | "any">("all");
	const [filters, setFilters] = useState<FilterClause[]>([]);
	const [sortField, setSortField] = useState("popularity_score");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
	const [limit, setLimit] = useState(100);

	const [result, setResult] = useState<PreviewResult | null>(null);
	const [loading, setLoading] = useState(true);
	const [previewError, setPreviewError] = useState<string | null>(null);

	const [saveOpen, setSaveOpen] = useState(false);
	const [saveName, setSaveName] = useState("");
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saveOk, setSaveOk] = useState(false);

	// Fields and segments load independently — a broken /planner/segments call
	// must not block the filter builder from rendering.
	useEffect(() => {
		let cancelled = false;
		fetchJson<FieldDescriptor[]>("/planner/fields")
			.then((f) => {
				if (!cancelled) setFields(f);
			})
			.catch((e: Error) => {
				if (!cancelled) setFieldsError(e.message);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		fetchJson<{ items: SegmentSummary[] }>("/planner/segments?limit=100")
			.then((r) => {
				if (!cancelled) setSegments(r.items);
			})
			.catch(() => {
				/* the "not in segment" filter just won't offer choices */
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const definition = useCallback(
		(): SegmentDefinition => ({
			match,
			filters,
			sort: { field: sortField, dir: sortDir },
			limit,
		}),
		[match, filters, sortField, sortDir, limit],
	);

	// Tab 1 opens on the raw nightly set (no spread filter, no filters at all)
	// and re-queries 300ms after the last edit — CssStatus.tsx refires per
	// keystroke, which this deliberately does not repeat.
	useEffect(() => {
		let cancelled = false;
		const timer = setTimeout(() => {
			setLoading(true);
			sendJson<PreviewResult>("/planner/preview", "POST", definition())
				.then((r) => {
					if (cancelled) return;
					setResult(r);
					setPreviewError(null);
				})
				.catch((e: Error) => {
					if (cancelled) return;
					setResult(null);
					setPreviewError(e.message);
				})
				.finally(() => {
					if (!cancelled) setLoading(false);
				});
		}, DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [definition]);

	const addFilter = (field = fields[0]?.key ?? "") => {
		const descriptor = fields.find((f) => f.key === field);
		if (!descriptor) return;
		setFilters((prev) => [
			...prev,
			{ field, op: descriptor.ops[0], value: undefined },
		]);
	};

	const addAttributeFilter = (field: string, value: string) => {
		setFilters((prev) => [...prev, { field, op: "eq", value }]);
	};

	const applyPreset = (presetDefinition: SegmentDefinition) => {
		setMatch(presetDefinition.match ?? "all");
		setFilters(presetDefinition.filters ?? []);
		if (presetDefinition.sort) {
			setSortField(presetDefinition.sort.field);
			setSortDir(presetDefinition.sort.dir ?? "desc");
		} else {
			setSortField("popularity_score");
			setSortDir("desc");
		}
	};

	const saveSegment = async () => {
		if (!saveName.trim()) return;
		setSaving(true);
		setSaveError(null);
		try {
			await sendJson("/planner/segments", "POST", {
				name: saveName.trim(),
				definition: definition(),
			});
			setSaveOk(true);
			setSaveName("");
			setTimeout(() => setSaveOk(false), 3000);
			setSaveOpen(false);
		} catch (e) {
			setSaveError(e instanceof Error ? e.message : String(e));
		} finally {
			setSaving(false);
		}
	};

	const sortableFields = fields.filter((f) => f.sortable);

	return (
		<div className="space-y-4">
			{fieldsError && (
				<ErrorBanner message={`Filter fields unavailable: ${fieldsError}`} />
			)}

			<div className="flex flex-wrap gap-2">
				{PLANNER_PRESETS.map((p) => (
					<button
						key={p.id}
						type="button"
						title={p.description}
						onClick={() => applyPreset(p.definition)}
						className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-primary hover:text-primary"
					>
						{p.label}
					</button>
				))}
			</div>

			<div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
				<div className="mb-3 flex items-center justify-between">
					<div className="flex items-center gap-2">
						<span className="text-sm font-semibold text-slate-700">
							Filters
						</span>
						<select
							value={match}
							onChange={(e) => setMatch(e.target.value as "all" | "any")}
							className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
						>
							<option value="all">Match all</option>
							<option value="any">Match any</option>
						</select>
					</div>
					<button
						type="button"
						onClick={() => addFilter()}
						disabled={fields.length === 0}
						className="flex items-center gap-1 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20 disabled:opacity-40"
					>
						<Plus className="h-3.5 w-3.5" /> Add filter
					</button>
				</div>

				<div className="space-y-2">
					{filters.length === 0 && (
						<p className="text-sm text-slate-400">
							No filters — showing the raw nightly set.
						</p>
					)}
					{filters.map((f, i) => (
						<FilterRow
							// biome-ignore lint/suspicious/noArrayIndexKey: filters have no stable id and are only ever appended/removed by index within this render
							key={i}
							filter={f}
							fields={fields}
							segments={segments}
							onChange={(next) =>
								setFilters((prev) =>
									prev.map((p, idx) => (idx === i ? next : p)),
								)
							}
							onRemove={() =>
								setFilters((prev) => prev.filter((_, idx) => idx !== i))
							}
						/>
					))}
				</div>

				<div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3 text-xs text-slate-500">
					<label className="flex items-center gap-1.5">
						Sort
						<select
							value={sortField}
							onChange={(e) => setSortField(e.target.value)}
							className="rounded-lg border border-slate-200 px-2 py-1"
						>
							{sortableFields.map((f) => (
								<option key={f.key} value={f.key}>
									{f.label}
								</option>
							))}
						</select>
						<select
							value={sortDir}
							onChange={(e) => setSortDir(e.target.value as "asc" | "desc")}
							className="rounded-lg border border-slate-200 px-2 py-1"
						>
							<option value="desc">High → low</option>
							<option value="asc">Low → high</option>
						</select>
					</label>
					<label className="flex items-center gap-1.5">
						Limit
						<input
							type="range"
							min={1}
							max={500}
							value={limit}
							onChange={(e) => setLimit(Number(e.target.value))}
							className="w-32"
						/>
						<span className="w-10 tabular-nums text-slate-700">{limit}</span>
					</label>

					<div className="ml-auto flex items-center gap-2">
						{loading && (
							<Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
						)}
						<span className="tabular-nums text-slate-600">
							{result ? `${nf.format(result.total)} match` : "—"}
						</span>
						{result?.truncated && (
							<span className="rounded-full bg-warning/10 px-2 py-0.5 text-warning-dark">
								showing first {limit}
							</span>
						)}
					</div>
				</div>
			</div>

			<div className="flex items-center justify-end gap-2">
				{saveOk && <span className="text-xs text-success">Saved.</span>}
				{saveOpen ? (
					<div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
						<input
							value={saveName}
							onChange={(e) => setSaveName(e.target.value)}
							placeholder="Segment name"
							className="rounded-lg border border-slate-200 px-2 py-1 text-sm"
						/>
						<button
							type="button"
							disabled={saving || !saveName.trim()}
							onClick={saveSegment}
							className="rounded-full bg-brand hover:bg-brand-hover px-3 py-1 text-xs font-semibold text-black disabled:opacity-40"
						>
							{saving ? "Saving…" : "Save"}
						</button>
						<button
							type="button"
							onClick={() => setSaveOpen(false)}
							className="text-xs text-slate-400 hover:text-slate-600"
						>
							Cancel
						</button>
					</div>
				) : (
					<button
						type="button"
						onClick={() => setSaveOpen(true)}
						className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:border-primary hover:text-primary"
					>
						<Save className="h-4 w-4" /> Save as segment
					</button>
				)}
			</div>
			{saveError && <ErrorBanner message={saveError} />}

			<div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
							<tr>
								<th className="px-4 py-2.5 font-medium">Product</th>
								<th className="px-4 py-2.5 font-medium">Merchants</th>
								<th className="px-4 py-2.5 font-medium">Price</th>
								<th className="px-4 py-2.5 font-medium">Content</th>
								<th className="px-4 py-2.5 font-medium">Rating</th>
								<th className="px-4 py-2.5 font-medium">Attributes</th>
								<th className="px-4 py-2.5 font-medium">CSS</th>
								<th className="px-4 py-2.5 font-medium">Traffic</th>
							</tr>
						</thead>
						<tbody>
							{!loading && previewError && (
								<tr>
									<td colSpan={8} className="px-4 py-6">
										<ErrorBanner message={previewError} />
									</td>
								</tr>
							)}
							{!loading && !previewError && result?.items.length === 0 && (
								<tr>
									<td
										colSpan={8}
										className="px-4 py-8 text-center text-slate-400"
									>
										No products match.
									</td>
								</tr>
							)}
							{loading && !result && (
								<tr>
									<td
										colSpan={8}
										className="px-4 py-8 text-center text-slate-400"
									>
										Loading…
									</td>
								</tr>
							)}
							{result?.items.map((row) => (
								<ProductRow
									key={row.productId}
									row={row}
									onAttributeClick={addAttributeFilter}
								/>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};
